import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Clock, Stethoscope, Heart, CheckCircle2 } from 'lucide-react';

const WhyChooseSection: React.FC = () => {
  const reasons = [
    {
      icon: <Award className="w-8 h-8 text-golden-yellow" />,
      title: "Specialized Expertise",
      description: "ASTS Fellowship trained in Liver/Kidney Transplant and advanced Hepatobiliary Surgery with special focus on Intestinal Transplant procedures."
    },
    {
      icon: <Users className="w-8 h-8 text-golden-yellow" />,
      title: "Patient-Centric Approach",
      description: "Dedicated to providing personalized care plans tailored to each patient's unique needs and circumstances."
    },
    {
      icon: <Clock className="w-8 h-8 text-golden-yellow" />,
      title: "Extensive Experience",
      description: "Over 1000+ successful surgeries performed, maintaining a remarkable 98% success rate in complex liver procedures."
    },
    {
      icon: <Stethoscope className="w-8 h-8 text-golden-yellow" />,
      title: "Comprehensive Care",
      description: "Expertise across the full spectrum of liver care, from routine treatments to complex transplant procedures."
    },
    {
      icon: <Heart className="w-8 h-8 text-golden-yellow" />,
      title: "Compassionate Care",
      description: "Committed to providing emotional support and clear communication throughout the treatment journey."
    },
    {
      icon: <CheckCircle2 className="w-8 h-8 text-golden-yellow" />,
      title: "Advanced Techniques",
      description: "Utilizes cutting-edge surgical techniques and modern treatment approaches for optimal patient outcomes."
    }
  ];

  return (
    <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-sage/5 to-softwhite relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 0.05, scale: 1 }}
          transition={{ duration: 1 }}
          className="absolute -left-64 -bottom-64 w-[500px] h-[500px] rounded-full bg-darkbrown"
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-darkbrown mb-4">
            Why Choose Dr. Hirak Pahari
          </h2>
          <p className="text-earthybeige text-lg">
            Combining advanced expertise with compassionate care for optimal patient outcomes
          </p>
        </motion.div>

        {/* Reasons Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-softwhite rounded-xl p-6 shadow-lg hover:shadow-xl transition-all"
            >
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-sage/10 rounded-lg">
                  {reason.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-darkbrown mb-2">
                    {reason.title}
                  </h3>
                  <p className="text-earthybeige">
                    {reason.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <p className="text-lg text-darkbrown mb-6">
            Experience world-class liver care with personalized attention
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-golden-yellow hover:bg-charcol text-white px-8 py-3 rounded-lg font-semibold transition-colors"
          >
            Schedule a Consultation
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseSection;